// src/components/settings/shared/AdvancedSection.jsx
import React from 'react';
import { Code, Database, Shield, Zap, Download, Upload, RefreshCw } from 'lucide-react';
import { FormField, TextInput, TextArea, Toggle, SelectInput } from './FormComponents';

export const AdvancedSection = ({ formData, onInputChange, onExport, onImport }) => {
  const backupFrequencies = [
    { value: 'never', label: 'Never' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const cacheOptions = [
    { value: 'none', label: 'No Caching' },
    { value: 'browser', label: 'Browser Cache Only' },
    { value: 'cdn', label: 'CDN Caching' },
    { value: 'full', label: 'Full Caching' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Advanced Settings</h3>
        <p className="text-gray-600">
          Developer tools and advanced configuration options
        </p>
      </div>

      {/* API & Development */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Code className="h-5 w-5 text-purple-600" />
          <h4 className="text-lg font-semibold text-gray-900">API & Development</h4>
        </div>
        
        <div className="space-y-4">
          <Toggle
            checked={formData.enableAPI}
            onChange={(value) => onInputChange('enableAPI', value)}
            label="Enable API Access"
            description="Allow external applications to access your data via API"
          />

          {formData.enableAPI && (
            <div className="bg-purple-50 p-4 rounded-lg space-y-4">
              <FormField label="API Key">
                <div className="flex gap-2">
                  <TextInput
                    value={formData.apiKey}
                    onChange={(value) => onInputChange('apiKey', value)}
                    placeholder="Your API key will appear here"
                    readOnly
                    type="password"
                  />
                  <button
                    onClick={() => onInputChange('apiKey', generateApiKey())}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </FormField>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Toggle
                  checked={formData.apiReadAccess}
                  onChange={(value) => onInputChange('apiReadAccess', value)}
                  label="Read Access"
                  description="Allow reading data via API"
                />

                <Toggle
                  checked={formData.apiWriteAccess}
                  onChange={(value) => onInputChange('apiWriteAccess', value)}
                  label="Write Access"
                  description="Allow modifying data via API"
                />
              </div>

              <FormField 
                label="Allowed Origins" 
                description="Domains allowed to make API requests (one per line)"
              >
                <TextArea
                  value={formData.allowedOrigins}
                  onChange={(value) => onInputChange('allowedOrigins', value)}
                  placeholder="https://yourdomain.com&#10;https://subdomain.yourdomain.com"
                  rows={3}
                />
              </FormField>
            </div>
          )}
        </div>
      </div>

      {/* Custom Code */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Code className="h-5 w-5 text-gray-600" />
          <h4 className="text-lg font-semibold text-gray-900">Custom Code</h4>
        </div>
        
        <div className="space-y-4">
          <FormField 
            label="Custom JavaScript" 
            description="Custom JavaScript code to include on your pages"
          >
            <TextArea
              value={formData.customJavaScript}
              onChange={(value) => onInputChange('customJavaScript', value)}
              placeholder="// Your custom JavaScript here"
              rows={6}
              className="font-mono text-sm"
            />
          </FormField>

          <FormField 
            label="Custom HTML Head" 
            description="Custom HTML to inject into the page head"
          >
            <TextArea
              value={formData.customHeadHTML}
              onChange={(value) => onInputChange('customHeadHTML', value)}
              placeholder="<!-- Custom head HTML -->"
              rows={4}
              className="font-mono text-sm"
            />
          </FormField>

          <FormField 
            label="Custom HTML Body" 
            description="Custom HTML to inject before closing body tag"
          >
            <TextArea
              value={formData.customBodyHTML}
              onChange={(value) => onInputChange('customBodyHTML', value)}
              placeholder="<!-- Custom body HTML -->"
              rows={4}
              className="font-mono text-sm"
            />
          </FormField>
        </div>
      </div>

      {/* Performance */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-600" />
          <h4 className="text-lg font-semibold text-gray-900">Performance</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Caching Strategy">
              <SelectInput
                value={formData.cacheStrategy}
                onChange={(value) => onInputChange('cacheStrategy', value)}
                options={cacheOptions}
              />
            </FormField>

            <FormField label="Cache TTL (hours)">
              <TextInput
                type="number"
                value={formData.cacheTTL}
                onChange={(value) => onInputChange('cacheTTL', parseInt(value))}
                placeholder="24"
                min="1"
                max="168"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Toggle
              checked={formData.enableCompression}
              onChange={(value) => onInputChange('enableCompression', value)}
              label="Enable Compression"
              description="Compress responses for faster loading"
            />

            <Toggle
              checked={formData.optimizeImages}
              onChange={(value) => onInputChange('optimizeImages', value)}
              label="Optimize Images"
              description="Automatically optimize uploaded images"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Toggle
              checked={formData.lazyLoading}
              onChange={(value) => onInputChange('lazyLoading', value)}
              label="Lazy Loading"
              description="Load images and content as needed"
            />

            <Toggle
              checked={formData.minifyAssets}
              onChange={(value) => onInputChange('minifyAssets', value)}
              label="Minify Assets"
              description="Compress CSS and JavaScript files"
            />
          </div>
        </div>
      </div>

      {/* Backup & Export */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-semibold text-gray-900">Backup & Data Management</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Toggle
              checked={formData.enableBackups}
              onChange={(value) => onInputChange('enableBackups', value)}
              label="Automatic Backups"
              description="Regularly backup your data"
            />

            {formData.enableBackups && (
              <FormField label="Backup Frequency">
                <SelectInput
                  value={formData.backupFrequency}
                  onChange={(value) => onInputChange('backupFrequency', value)}
                  options={backupFrequencies}
                />
              </FormField>
            )}
          </div>

          <div className="flex gap-4">
            <button
              onClick={onExport}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Data
            </button>

            <label className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 cursor-pointer">
              <Upload className="h-4 w-4" />
              Import Data
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => onImport?.(e.target.files[0])}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-red-600" />
          <h4 className="text-lg font-semibold text-gray-900">Security</h4>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Toggle
              checked={formData.enableTwoFactor}
              onChange={(value) => onInputChange('enableTwoFactor', value)}
              label="Two-Factor Authentication"
              description="Require 2FA for account access"
            />

            <Toggle
              checked={formData.enableRateLimiting}
              onChange={(value) => onInputChange('enableRateLimiting', value)}
              label="Rate Limiting"
              description="Limit API requests per hour"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Toggle
              checked={formData.enableFirewall}
              onChange={(value) => onInputChange('enableFirewall', value)}
              label="Web Application Firewall"
              description="Protection against common attacks"
            />

            <Toggle
              checked={formData.logSecurityEvents}
              onChange={(value) => onInputChange('logSecurityEvents', value)}
              label="Security Event Logging"
              description="Log security-related events"
            />
          </div>

          {formData.enableRateLimiting && (
            <FormField label="Rate Limit (requests per hour)">
              <TextInput
                type="number"
                value={formData.rateLimit}
                onChange={(value) => onInputChange('rateLimit', parseInt(value))}
                placeholder="1000"
                min="10"
                max="10000"
              />
            </FormField>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">System Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">API Status</div>
            <div className="font-semibold text-green-600">Operational</div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">Last Backup</div>
            <div className="font-semibold text-gray-900">
              {formData.lastBackup ? new Date(formData.lastBackup).toLocaleDateString() : 'Never'}
            </div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">Storage Used</div>
            <div className="font-semibold text-blue-600">
              {formData.storageUsed || '0'} MB
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to generate API key
const generateApiKey = () => {
  return 'pb_' + Math.random().toString(36).substr(2, 32);
};